import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Stack,
  IconButton,
  Chip,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Launch as LaunchIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface ReportGenerationModalProps {
  open: boolean;
  onClose: () => void;
  reportType: 'Initial' | 'Review';
  isGenerating: boolean;
  progress: number;
  reportUrl?: string;
  error?: string | null;
  onRetry?: () => void;
}

export const ReportGenerationModal: React.FC<ReportGenerationModalProps> = ({
  open,
  onClose,
  reportType,
  isGenerating,
  progress,
  reportUrl,
  error,
  onRetry,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (progress === 100 && !error) {
      setShowSuccess(true);
    }
  }, [progress, error]);

  const getProgressMessage = () => {
    if (error) return 'An error occurred';
    if (progress === 100) return 'Report generated successfully!';
    if (progress < 30) return 'Collecting client data...';
    if (progress < 50) return 'Preparing assessment information...';
    if (progress < 70) return 'Generating report document...';
    if (progress < 85) return 'Formatting and finalizing...';
    if (progress < 90) return 'Almost complete...';
    return 'Finalizing report...';
  };

  const handleCopyLink = () => {
    if (reportUrl) {
      navigator.clipboard.writeText(reportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      setShowSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'visible',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">
              Generate {reportType} Assessment Report
            </Typography>
          </Box>
          {!isGenerating && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Progress Section */}
          {(isGenerating || progress > 0) && !error && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: '100%', mr: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 1,
                        bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                      },
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 45 }}>
                  <Typography variant="body2" color="text.secondary">
                    {`${Math.round(progress)}%`}
                  </Typography>
                </Box>
              </Box>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={getProgressMessage()}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="body2"
                    color={progress === 100 ? 'success.main' : 'text.secondary'}
                    align="center"
                  >
                    {getProgressMessage()}
                  </Typography>
                </motion.div>
              </AnimatePresence>
            </Box>
          )}

          {/* Success State */}
          {showSuccess && reportUrl && (
            <Fade in={showSuccess}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        fontSize: 64,
                        color: 'success.main',
                      }}
                    />
                  </motion.div>
                </Box>

                <Stack spacing={2}>
                  <Alert severity="success" sx={{ borderRadius: 1 }}>
                    Your {reportType.toLowerCase()} assessment report has been generated successfully!
                  </Alert>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Report URL
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {reportUrl}
                      </Typography>
                      <IconButton size="small" onClick={handleCopyLink}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {copied && (
                      <Fade in={copied}>
                        <Typography variant="caption" color="success.main">
                          Copied to clipboard!
                        </Typography>
                      </Fade>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Fade>
          )}

          {/* Error State */}
          {error && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              {onRetry && (
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={onRetry}
                    startIcon={<AssessmentIcon />}
                  >
                    Retry Generation
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Info Section */}
          {isGenerating && !error && (
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              <Typography variant="body2">
                Report generation typically takes 2-3 minutes. Please don't close this window.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        {reportUrl && !isGenerating ? (
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Report
            </Button>
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Report
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            disabled={isGenerating}
            variant={error ? 'contained' : 'text'}
          >
            {error ? 'Close' : isGenerating ? 'Please wait...' : 'Cancel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};